/* Rich text for Compose Email — a port of the client's assets/rich-editor.js
   (spec/33). The editor itself is TipTap (components/common/RichEditor); these
   helpers are the contract around it. The only tags that can leave the editor
   are p br b i u s a ul ol li, a link carries target="_blank" and
   rel="noopener noreferrer", and every attribute but href is dropped. Anything
   from outside — a paste from Word or Google Docs, a template, a stored draft —
   goes through the same sanitizer before it touches the page or an inbox. */

export const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
export const escAttr = (s) => esc(s).replace(/"/g, '&quot;');

const DROP = new Set(['SCRIPT', 'STYLE', 'HEAD', 'META', 'LINK', 'TITLE', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'CANVAS',
  'NOSCRIPT', 'TEMPLATE', 'IMG', 'PICTURE', 'VIDEO', 'AUDIO', 'INPUT', 'BUTTON', 'SELECT', 'TEXTAREA', 'FORM']);
const BLOCK = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE', 'SECTION', 'ARTICLE', 'HEADER',
  'FOOTER', 'FIGURE', 'TABLE', 'TBODY', 'THEAD', 'TR', 'TD', 'TH', 'DL', 'DT', 'DD']);
const OUT_BLOCK = new Set(['P', 'UL', 'OL']);
const SAFE_URL = /^(https?:|mailto:|tel:)/i;

/* An unfilled spot: a {{key}} a template form hasn't answered, or a [bracket]
   left in hand-written copy. Both show red and hold Send. */
export const PH_RE = /(\{\{[^}\n]+\}\}|\[[^\]\n]+\])/;

/* Word and Google Docs say "bold" in a style attribute on a span, and wrap whole
   documents in a <b style="font-weight:normal">. Read the style, not just the tag. */
function marksOf(el) {
  const st = el.style || {};
  const tag = el.tagName;
  if (tag === 'A') return []; // a link is underlined by the link, not by a mark
  const w = st.fontWeight;
  const weightSet = w !== undefined && w !== '';
  const bold = weightSet ? /^(bold|bolder|[6-9]00)$/.test(w) : tag === 'B' || tag === 'STRONG';
  const italic = st.fontStyle ? /italic|oblique/.test(st.fontStyle) : tag === 'I' || tag === 'EM' || tag === 'CITE';
  const deco = st.textDecorationLine || st.textDecoration || '';
  const under = /underline/.test(deco) || (tag === 'U' && !deco);
  const strike = /line-through/.test(deco) || ((tag === 'S' || tag === 'STRIKE' || tag === 'DEL') && !deco);
  return [bold && 'b', italic && 'i', under && 'u', strike && 's'].filter(Boolean);
}

/* https:, http:, mailto: and tel: only. A bare example.com gets https://, a
   bare address gets mailto:. Anything else (javascript:, ftp:) comes back ''. */
export function normalizeUrl(raw) {
  let u = String(raw || '').trim();
  if (!u) return '';
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(u)) return 'mailto:' + u;
  if (!/^[a-z][a-z0-9+.-]*:/i.test(u)) u = 'https://' + u;
  if (!SAFE_URL.test(u)) return '';
  try {
    // eslint-disable-next-line no-new
    new URL(u);
  } catch {
    return '';
  }
  return u;
}

/* Takes an HTML string, or a live element. */
export function sanitize(input) {
  const src =
    typeof input === 'string'
      ? new DOMParser().parseFromString('<body>' + input + '</body>', 'text/html').body
      : input.cloneNode(true);
  const mk = (n) => document.createElement(n);
  const isBlockNode = (n) => n.nodeType === 1 && OUT_BLOCK.has(n.tagName);

  function walk(n, inPre) {
    if (n.nodeType === 3) return [document.createTextNode(inPre ? n.nodeValue : n.nodeValue.replace(/[ \t\r\n\f]+/g, ' '))];
    if (n.nodeType !== 1) return [];
    const tag = n.tagName;
    if (DROP.has(tag)) return [];
    if (tag === 'BR') return [mk('br')];
    const kids = () => [].concat(...[...n.childNodes].map((c) => walk(c, inPre || tag === 'PRE')));

    if (tag === 'UL' || tag === 'OL') {
      const list = mk(tag.toLowerCase());
      kids().forEach((k) => {
        if (k.nodeType === 1 && k.tagName === 'LI') list.appendChild(k);
        else if (k.nodeType === 1 || k.textContent.trim()) {
          const li = mk('li');
          li.appendChild(k);
          list.appendChild(li);
        }
      });
      return list.childNodes.length ? [list] : [];
    }
    if (tag === 'LI') {
      const li = mk('li');
      kids().forEach((k) => {
        if (k.nodeType === 1 && k.tagName === 'P') {
          /* <li><p>..</p></li>: the paragraph is just the item */
          if (li.childNodes.length) li.appendChild(mk('br'));
          [...k.childNodes].forEach((c) => li.appendChild(c));
        } else li.appendChild(k);
      });
      return [li];
    }
    if (BLOCK.has(tag)) {
      const nodes = kids();
      if (nodes.some(isBlockNode)) return nodes; // already holds blocks: just unwrap it
      const p = mk('p');
      nodes.forEach((k) => p.appendChild(k));
      return [p];
    }

    let cur = kids();
    if (tag === 'A') {
      const href = normalizeUrl(n.getAttribute('href'));
      if (href) {
        const a = mk('a');
        a.setAttribute('href', href);
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
        cur.forEach((k) => a.appendChild(k));
        cur = [a];
      }
    }
    marksOf(n).reverse().forEach((m) => {
      const w = mk(m);
      cur.forEach((k) => w.appendChild(k));
      cur = [w];
    });
    return cur;
  }

  /* top level: every run of loose inline nodes becomes a paragraph */
  const out = mk('div');
  let loose = null;
  const flush = () => {
    if (loose) {
      if (loose.textContent.trim() || loose.querySelector('br,a')) out.appendChild(loose);
      loose = null;
    }
  };
  [].concat(...[...src.childNodes].map((c) => walk(c, false))).forEach((n) => {
    if (isBlockNode(n)) {
      flush();
      out.appendChild(n);
    } else {
      if (!loose) loose = mk('p');
      loose.appendChild(n);
    }
  });
  flush();

  /* trim the empty paragraphs a paste leaves at either end, and the spaces at paragraph edges */
  const empty = (p) => p.tagName === 'P' && !p.textContent.trim() && !p.querySelector('a');
  while (out.firstChild && empty(out.firstChild)) out.removeChild(out.firstChild);
  while (out.lastChild && empty(out.lastChild)) out.removeChild(out.lastChild);
  out.querySelectorAll('p').forEach((p) => {
    if (p.firstChild && p.firstChild.nodeType === 3) p.firstChild.nodeValue = p.firstChild.nodeValue.replace(/^ +/, '');
    if (p.lastChild && p.lastChild.nodeType === 3) p.lastChild.nodeValue = p.lastChild.nodeValue.replace(/ +$/, '');
    if (!p.textContent && !p.querySelector('br,a')) p.innerHTML = '<br>'; // a blank line stays a blank line
  });
  return out.innerHTML;
}

/* plain text with blank lines between paragraphs → paragraphs, line breaks inside them */
export function textToHtml(text) {
  return String(text || '')
    .replace(/\r\n?/g, '\n')
    .trim()
    .split(/\n{2,}/)
    .filter(Boolean)
    .map((p) => '<p>' + esc(p).replace(/\n/g, '<br>') + '</p>')
    .join('');
}

/* HTML → the plain-text version of the email: blank line between paragraphs,
   bullets for lists, and a link keeps its address in brackets so nothing is
   lost in a text-only mail app */
export function htmlToText(html) {
  const root = new DOMParser().parseFromString('<body>' + (html || '') + '</body>', 'text/html').body;
  const t = (n) => {
    if (n.nodeType === 3) return n.nodeValue.replace(/ /g, ' ');
    if (n.nodeType !== 1) return '';
    const tag = n.tagName.toLowerCase();
    const inner = () => [...n.childNodes].map(t).join('');
    if (tag === 'br') return '\n';
    if (tag === 'p') return inner() + '\n\n';
    if (tag === 'ul' || tag === 'ol') {
      let i = 0;
      return (
        [...n.children]
          .map((li) => {
            i += 1;
            return (tag === 'ol' ? i + '. ' : '• ') + [...li.childNodes].map(t).join('').trim() + '\n';
          })
          .join('') + '\n'
      );
    }
    if (tag === 'a') {
      const text = inner();
      const href = n.getAttribute('href') || '';
      return !href || text.trim() === href || 'mailto:' + text.trim() === href ? text : `${text} (${href})`;
    }
    return inner();
  };
  return t(root).replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/* A template's body (plain text, {{key}} tokens) as editor HTML, every token
   wrapped in a field span the editor can find again. The spans never leave the
   editor — sanitize() unwraps them. */
export function templateBodyToHtml(body, fields = []) {
  const keys = fields.map((f) => f.key).filter(Boolean);
  const tokenRe = keys.length ? new RegExp(`\\{\\{(${keys.map(escapeRe).join('|')})\\}\\}`, 'g') : null;
  return String(body || '')
    .replace(/\r\n?/g, '\n')
    .trim()
    .split(/\n{2,}/)
    .filter(Boolean)
    .map((p) => {
      let html = esc(p).replace(/\n/g, '<br>');
      if (tokenRe) html = html.replace(tokenRe, (m, key) => `<span data-f="${escAttr(key)}">${m}</span>`);
      return `<p>${html}</p>`;
    })
    .join('');
}

/* The three decorations the preview and the real send both apply, on text
   nodes and never inside a link the room parent made: the first HERE becomes
   the consent-form link, placeholders turn red (preview only), and a template's
   date reads bold as a deadline. None of them is stored in the draft. */
export function decorateHtml(html, { consentUrl = '', deadlines = [], markPlaceholders = false } = {}) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString('<body>' + html + '</body>', 'text/html');
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const nodes = [];
  let n;
  while ((n = walker.nextNode())) if (!n.parentElement.closest('a')) nodes.push(n);

  let hereDone = false;
  const phRe = new RegExp(PH_RE.source, 'g');
  nodes.forEach((node) => {
    const text = node.nodeValue;
    const hits = [];
    if (consentUrl && !hereDone) {
      const m = /\bHERE\b/.exec(text);
      if (m) hits.push({ start: m.index, end: m.index + 4, kind: 'here' });
    }
    if (markPlaceholders) {
      let m;
      phRe.lastIndex = 0;
      while ((m = phRe.exec(text))) hits.push({ start: m.index, end: m.index + m[0].length, kind: 'ph' });
    }
    deadlines.filter(Boolean).forEach((d) => {
      let at = text.indexOf(d);
      while (at >= 0) {
        hits.push({ start: at, end: at + d.length, kind: 'b' });
        at = text.indexOf(d, at + d.length);
      }
    });
    if (!hits.length) return;
    hits.sort((a, b) => a.start - b.start);
    const frag = doc.createDocumentFragment();
    let pos = 0;
    hits.forEach((h) => {
      if (h.start < pos) return; // overlaps one already applied
      if (h.start > pos) frag.appendChild(doc.createTextNode(text.slice(pos, h.start)));
      const piece = text.slice(h.start, h.end);
      let el;
      if (h.kind === 'here') {
        el = doc.createElement('a');
        el.setAttribute('href', consentUrl);
        el.setAttribute('target', '_blank');
        el.setAttribute('rel', 'noreferrer');
        hereDone = true;
      } else if (h.kind === 'ph') {
        el = doc.createElement('span');
        el.className = 'ph';
      } else {
        el = doc.createElement('b');
      }
      el.textContent = piece;
      frag.appendChild(el);
      pos = h.end;
    });
    if (pos < text.length) frag.appendChild(doc.createTextNode(text.slice(pos)));
    node.parentNode.replaceChild(frag, node);
  });
  return doc.body.innerHTML;
}

/* Mail clients ignore <style>, so the spacing a paragraph or list needs rides
   on the element itself at send time. */
export function emailBodyStyles(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString('<body>' + html + '</body>', 'text/html');
  doc.body.querySelectorAll('p').forEach((p) => p.setAttribute('style', 'margin:0 0 12px'));
  doc.body.querySelectorAll('ul,ol').forEach((l) => l.setAttribute('style', 'margin:0 0 12px;padding-left:24px'));
  doc.body.querySelectorAll('a').forEach((a) => a.setAttribute('style', 'color:#186338'));
  return doc.body.innerHTML;
}
